// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What a lamp state looks like (M3-15).
//
// The rule engine stores a lamp as one of six small integers — `off`, `on`, `slow`, `fast`,
// `pulse`, `strobe` — and deliberately knows nothing about what any of them looks like. That
// is the right split: a blink rate is a presentation decision, and putting it in the engine
// would put it in the replay-validated core, where changing it later would invalidate every
// score ever recorded.
//
// So brightness is decided here, and it is a function of the **step index**. Not a clock. A
// lamp blinking off `performance.now()` drifts away from the game that is driving it whenever
// the renderer stutters, and the tell is subtle and maddening: the lamp for a shot goes dark
// for a moment exactly when you make the shot, because the blink and the award are being
// timed by two different things.

/** The codes `RuleEngine` stores. Mirrored here rather than imported — see below. */
export const LAMP_OFF = 0
export const LAMP_ON = 1
export const LAMP_SLOW = 2
export const LAMP_FAST = 3
export const LAMP_PULSE = 4
export const LAMP_STROBE = 5
export const LAMP_CHASE = 6

// The engine's `LAMP_CODES` table is private and the numbers are part of its fingerprint, so
// they are stable by construction — a change to them changes every replay checksum, which is
// a change nobody makes by accident. Mirroring them keeps the renderer from reaching into the
// engine's internals for a presentation decision.

/**
 * Steps in one cycle of each blink. A step is a millisecond at 1000 Hz.
 *
 * **Every one of these is longer than 345 steps, and that is a safety rule rather than a taste**
 * (NFR-026): nothing in this product may flash between 3 Hz and 55 Hz at high contrast, and a
 * playfield is sixty inserts flashing together over most of a phone's screen. `fast` was 220
 * steps — **4.5 Hz, squarely inside the band** — and `strobe` was 260, which is 3.8 Hz. Both were
 * written as presentation numbers by somebody thinking about how a machine looks, which is
 * exactly how this class of defect arrives: nothing fails, and the people it hurts are not in
 * the room.
 *
 * Both are 2.86 Hz now. A `fast` blink at just under three a second still reads as fast —
 * the difference against 4.5 is barely nameable side by side — and it is on the safe side of a
 * line that exists for a reason. `MIN_BLINK_PERIOD` is checked by a test, so the next person to
 * tune one of these for feel finds out immediately.
 */
const SLOW_PERIOD = 700
const FAST_PERIOD = 350
const PULSE_PERIOD = 900
const STROBE_PERIOD = 350
/** Steps a strobe is actually lit. Short, because that is what makes it read as a strobe. */
const STROBE_ON = 44

/**
 * No lamp state may cycle faster than this.
 *
 * 1000 / 350 ≈ 2.86 Hz. Under NFR-026's 3 Hz floor with about five per cent to spare, which is
 * the point: a limit sitting exactly on the line is one that the next rounding decision crosses.
 */
export const MIN_BLINK_PERIOD = 350

/** Every period a lamp state can run at, for the test that keeps them out of the band. */
export const BLINK_PERIODS: readonly number[] = [
  SLOW_PERIOD,
  FAST_PERIOD,
  PULSE_PERIOD,
  STROBE_PERIOD,
  FAST_PERIOD * 2,
]

/**
 * How dark a lamp is allowed to get while flash reduction is on.
 *
 * The rate is only half of what makes a flash a hazard; the other half is contrast. A blink that
 * swings from nothing to full is a hard edge whatever its rate, so under reduction the lamp
 * breathes between this and full instead of switching. It still says everything it was saying —
 * a blinking insert is still visibly doing something different from a steady one — which is the
 * whole test of an accessibility mode: it must cost comfort, never information.
 */
export const REDUCED_FLOOR = 0.45

/**
 * Brightness 0…1 for a lamp state at a step.
 *
 * `phase` shifts the cycle, and exists for one state: a chase is a group of lamps running the
 * same pattern at staggered offsets. Every other state ignores it, so a machine that never
 * chases never pays it a thought.
 */
export function lampBrightness(code: number, step: number, phase = 0, reduced = false): number {
  if (reduced) return reducedBrightness(code, step, phase)
  switch (code) {
    case LAMP_OFF:
      return 0
    case LAMP_ON:
      return 1
    case LAMP_SLOW:
      return square(step, SLOW_PERIOD, phase)
    case LAMP_FAST:
      return square(step, FAST_PERIOD, phase)
    case LAMP_PULSE:
      // A breathe, not a flash. `pulse` is a *state* the engine holds until something changes
      // it, so it has to be something a lamp can do indefinitely without looking broken; a
      // repeated hard flash for ninety seconds of a mode is a fault light, not an invitation.
      return 0.25 + 0.75 * (0.5 - 0.5 * Math.cos((2 * Math.PI * cycle(step, PULSE_PERIOD, phase))))
    case LAMP_STROBE:
      return cycle(step, STROBE_PERIOD, phase) * STROBE_PERIOD < STROBE_ON ? 1 : 0
    case LAMP_CHASE:
      // The engine collapses `chase:<group>` to a bare code, so the group is gone by the time
      // it reaches here — which is why the caller supplies `phase` from the lamp's position
      // within its own declared group. A chase whose lamps all share a phase is a blink, and
      // that is the honest fallback for a lamp that belongs to no group at all.
      return square(step, FAST_PERIOD * 2, phase)
    default:
      // An unknown code lights the lamp rather than blanking it. A machine using a state this
      // build has never heard of is better served by a lamp that is on — visibly odd, and the
      // shot is still findable — than by one that is dark, which is indistinguishable from a
      // rule that never fired.
      return 1
  }
}

/**
 * The same states, with the flashing taken out (NFR-026).
 *
 * Every blink becomes the same slow breathe between `REDUCED_FLOOR` and full, at the state's own
 * rate. **Not "everything goes steady"**, which would be the easy version and the wrong one: the
 * difference between a lit shot and a blinking one is *information* — it is the machine saying
 * "this one is worth something right now" — and an accessibility setting that quietly removes it
 * has taken the game away from the player it was meant to help.
 */
function reducedBrightness(code: number, step: number, phase: number): number {
  switch (code) {
    case LAMP_OFF:
      return 0
    case LAMP_ON:
      return 1
    case LAMP_SLOW:
      return breathe(step, SLOW_PERIOD, phase)
    case LAMP_FAST:
      return breathe(step, FAST_PERIOD, phase)
    case LAMP_PULSE:
      return breathe(step, PULSE_PERIOD, phase)
    case LAMP_STROBE:
      // The one state that cannot survive as itself. A strobe *is* the hard edge, so under
      // reduction it becomes the fastest breathe rather than a slower strobe.
      return breathe(step, STROBE_PERIOD, phase)
    case LAMP_CHASE:
      return breathe(step, FAST_PERIOD * 2, phase)
    default:
      return 1
  }
}

/** A cosine between the reduced floor and full. No edges anywhere in it. */
function breathe(step: number, period: number, phase: number): number {
  const t = cycle(step, period, phase)
  return REDUCED_FLOOR + (1 - REDUCED_FLOOR) * (0.5 - 0.5 * Math.cos(2 * Math.PI * t))
}

/** Position within the cycle, 0…1. */
function cycle(step: number, period: number, phase: number): number {
  const t = (step / period + phase) % 1
  return t < 0 ? t + 1 : t
}

/** On for the first half, off for the second, with softened edges so it does not buzz. */
function square(step: number, period: number, phase: number): number {
  const t = cycle(step, period, phase)
  // A hard square wave sampled at 60 Hz against a 220-step period aliases into a visible
  // flutter. Ten per cent of the cycle spent on each edge costs nothing and removes it.
  const edge = 0.1
  if (t < edge) return t / edge
  if (t < 0.5 - edge) return 1
  if (t < 0.5) return (0.5 - t) / edge
  return 0
}

/**
 * Phases for the lamps of one group, so a chase runs round it rather than blinking together.
 *
 * Returned as a map rather than computed per lamp, because the phase depends on how many
 * lamps share the group — which is a property of the machine, not of any one lamp.
 */
export function chasePhases(groups: ReadonlyMap<string, readonly string[]>): Map<string, number> {
  const out = new Map<string, number>()
  for (const [, members] of groups) {
    for (let i = 0; i < members.length; i++) out.set(members[i]!, i / members.length)
  }
  return out
}
